/*************************
 * @license
 * Copyright 2024 Myenergi Ltd. All rights reserved.
 * No part of this work may be reproduced, stored in a retrieval system of any nature, or transmitted, in any form or by any means without the prior written permission of Myenergi Ltd., the copyright owner.
 * If any unauthorised acts are carried out in relation to this copyright work, a civil claim for damages may be made and/or a criminal prosecution may result.
 *************************/
import { SerialNumber } from "@myenergi-ltd/static";
import { del, get, post, put } from "utils/apiMethods";
import {
  AccessGroupSearchParams,
  AccessGroupSearchResults,
  AccessManagementUserSearchResult,
  addAdditionalAccessGroupPost,
  AddDeviceToAccessGroupProps,
  AddNewPinPost,
  AddNewPinResponse,
  BaseResult,
  CheckSuperUserLinkResult,
  Customer,
  CustomerData,
  CustomerSearchParams,
  CustomerSearchResult,
  Device,
  DeviceReplacementData,
  DsrUserFlags,
  EditPin,
  GoodwillWarranty,
  HubAddress,
  LibbiComponentsValues,
  LibbiInvertersCountResult,
  RegistrationDateValues,
  RemoveAccessGroup,
  RemoveDeviceFromAccessGroup,
  RemovePin,
  WarrantyData,
} from "./types/Customers";

const customerDataApiRoot = process.env.CUSTOMER_DATA_API_ROOT;

class CustomerDataApi {
  getCustomerData = async (
    customerGuid: string,
    jwt: string,
  ): Promise<CustomerData> =>
    await get(`CustomerData/${customerGuid}`, customerDataApiRoot, jwt);

  cancelExtendedWarranty = async (
    customerGuid: string,
    serialNo: SerialNumber,
    jwt: string,
  ): Promise<CustomerData> => {
    await put(
      `CustomerData/CancelExtendedWarranty?serialNo=${serialNo}`,
      undefined,
      customerDataApiRoot,
      jwt,
    );

    return await this.getCustomerData(customerGuid, jwt);
  };

  GetDeviceData = async (
    hubSerialNo: SerialNumber,
    jwt: string,
  ): Promise<WarrantyData> => {
    const result = await get(
      `CustomerData/GetDeviceData?hubSerialNo=${hubSerialNo}`,
      customerDataApiRoot,
      jwt,
    );

    // map serial numbers to a number as that's what AP uses
    return {
      ...result,
      hubSerialNo: Number(result.hubSerialNo),
      devices: result.devices.map((d: Device) => ({
        ...d,
        serialNo: Number(d.serialNo),
      })),
    };
  };

  search = async (
    searchParams: CustomerSearchParams,
    jwt: string,
  ): Promise<CustomerSearchResult> => {
    const encodedSearch = Object.entries(searchParams)
      .map(([key, val]) => `${key}=${encodeURIComponent(val)}`)
      .join("&");

    return await get(
      `CustomerData/Search?${encodedSearch}`,
      customerDataApiRoot,
      jwt,
    );
  };

  getAccessManagementInfoByUser = async (
    paramsObj: { filter: string },
    jwt: string,
  ): Promise<AccessManagementUserSearchResult> => {
    const searchParams = new URLSearchParams(paramsObj);

    return await get(
      `CustomerData/AccessManagementInfoByUser?${searchParams}`,
      customerDataApiRoot,
      jwt,
    );
  };

  getAccessGroupInfo = async (
    paramsObj: AccessGroupSearchParams,
    jwt: string,
  ): Promise<AccessGroupSearchResults> => {
    const searchParams = new URLSearchParams(paramsObj);

    return await get(
      `CustomerData/PinConfigsByFriendlyName?${searchParams}`,
      customerDataApiRoot,
      jwt,
    );
  };

  updateCustomerPersonalDetails = async (
    { customerGuid, ...personalDetails }: Customer,
    jwt: string,
  ): Promise<CustomerData> => {
    await put(
      `CustomerData/${customerGuid}`,
      personalDetails,
      customerDataApiRoot,
      jwt,
    );

    return await this.getCustomerData(customerGuid, jwt);
  };

  deleteCustomer = async (customerGuid: string, jwt: string) => {
    await del(
      `CustomerData/DeleteAccount?userGuid=${customerGuid}`,
      customerDataApiRoot,
      jwt,
    );
  };

  updateCustomerHubAddress = async (
    customerGuid: string,
    { addressGuid, ...locationDetails }: HubAddress,
    jwt: string,
  ): Promise<CustomerData> => {
    await put(
      `CustomerData/Address/${addressGuid}`,
      locationDetails,
      customerDataApiRoot,
      jwt,
    );

    return await this.getCustomerData(customerGuid, jwt);
  };

  updateCustomerDsrPreferences = async (
    customerGuid: string,
    userFlags: DsrUserFlags,
    jwt: string,
  ): Promise<CustomerData> => {
    await put(
      `CustomerData/${customerGuid}`,
      userFlags,
      customerDataApiRoot,
      jwt,
    );

    return await this.getCustomerData(customerGuid, jwt);
  };

  updateDateOfPurchase = async (
    customerGuid: string,
    serialNo: SerialNumber,
    startDate: Date,
    isReplacedDevice: boolean,
    jwt: string,
  ): Promise<CustomerData> => {
    const postData = {
      serialNo: serialNo.toString(),
      dateOfPurchase: startDate.toISOString(),
      isReplacedDevice,
    };

    await post(
      "CustomerData/EditDateOfPurchase",
      postData,
      customerDataApiRoot,
      jwt,
    );

    return await this.getCustomerData(customerGuid, jwt);
  };

  updateGoodwillWarranty = async (
    deviceGuid: string,
    goodwillWarranty: GoodwillWarranty,
    jwt: string,
  ): Promise<BaseResult> => {
    const payload = {
      deviceId: deviceGuid,
      goodwillYears: goodwillWarranty.years,
      goodwillMonths: goodwillWarranty.months,
      goodwillReason: goodwillWarranty.reason,
      startDate: goodwillWarranty.startDate,
    };

    return await put(
      "CustomerData/GoodwillWarranty",
      payload,
      customerDataApiRoot,
      jwt,
    );
  };

  getReplacementData = async (
    newDeviceGuid: string,
    jwt: string,
  ): Promise<DeviceReplacementData> =>
    await get(
      `CustomerData/GetDeviceReplacement?newDeviceId=${newDeviceGuid}`,
      customerDataApiRoot,
      jwt,
    );

  replaceDevice = async (
    newDeviceGuid: string,
    oldDeviecSerialNo: number,
    jwt: string,
  ): Promise<DeviceReplacementData> => {
    const postData = {
      OldDeviceSerialNo: oldDeviecSerialNo.toString(),
      NewDeviceId: newDeviceGuid,
    };

    await put(
      "CustomerData/SaveDeviceReplacement",
      postData,
      customerDataApiRoot,
      jwt,
    );

    return await this.getReplacementData(newDeviceGuid, jwt);
  };

  deleteReplacement = async (newDeviceGuid: string, jwt: string) =>
    await del(
      `CustomerData/DeleteDeviceReplacement?newDeviceId=${newDeviceGuid}`,
      customerDataApiRoot,
      jwt,
    );

  deleteDevice = async (deviceGuid: string, jwt: string, hardDelete = false) =>
    await del(
      `CustomerData/Devices/DeleteDevice?deviceGuid=${deviceGuid}&hardDelete=${hardDelete}`,
      customerDataApiRoot,
      jwt,
    );

  /**
   * Deletes the hub from myaccount, unlinks its devices so they remain as devices without
   * a hub (AKA phantom devices), and deregisters the hub from the data server.
   */
  deleteHub = async (hubGuid: string, jwt: string) =>
    await del(
      `CustomerData/Devices/DeleteHub?hubGuid=${hubGuid}`,
      customerDataApiRoot,
      jwt,
    );

  linkSuperUserToClient = async (
    clientEmail: string,
    jwt: string,
  ): Promise<BaseResult> => {
    const payload = {
      ClientEmail: clientEmail,
    };

    return await put(
      "CustomerData/LinkSuperUserToClient",
      payload,
      customerDataApiRoot,
      jwt,
    );
  };

  unlinkSuperUserFromClient = async (clientEmail: string, jwt: string) => {
    const payload = {
      ClientEmail: clientEmail,
    };

    return await del(
      "CustomerData/UnlinkSuperUserFromClient",
      customerDataApiRoot,
      jwt,
      payload,
    );
  };

  checkSuperUserLinkage = async (
    clientEmail: string,
    jwt: string,
  ): Promise<CheckSuperUserLinkResult> => {
    const postData = {
      ClientEmail: clientEmail,
    };

    return await post(
      "CustomerData/CheckLinkageAvailability",
      postData,
      customerDataApiRoot,
      jwt,
    );
  };

  getLibbiInvertersCount = async (
    jwt: string,
  ): Promise<LibbiInvertersCountResult> =>
    await get(`CustomerData/LibbiInvertersNumber`, customerDataApiRoot, jwt);

  reconnectDeletedDevice = async (
    hubId: string, // myaccount hub GUID
    deviceSerial: number,
    jwt: string,
  ): Promise<BaseResult> => {
    const params = `?hubId=${hubId}&deviceSerial=${deviceSerial}`;

    return await put(
      `CustomerData/ReAddDevice${params}`,
      {},
      customerDataApiRoot,
      jwt,
    );
  };

  putLibbiComponents = async (
    serialNo: SerialNumber,
    values: LibbiComponentsValues,
    jwt: string,
  ): Promise<BaseResult> => {
    const payload = {
      inverter: {
        serialNo: values.inverterSerialNo,
        standardWarranty: values.inverterStartDate,
      },
      batteryOne: {
        serialNo: values.batteryOneSerialNo,
        standardWarranty: values.batteryOneStartDate,
      },
      batteryTwo: {
        serialNo: values.batteryTwoSerialNo,
        standardWarranty: values.batteryTwoStartDate,
      },
      batteryThree: {
        serialNo: values.batteryThreeSerialNo,
        standardWarranty: values.batteryThreeStartDate,
      },
      batteryFour: {
        serialNo: values.batteryFourSerialNo,
        standardWarranty: values.batteryFourStartDate,
      },
    };

    return await put(
      `CustomerData/LibbiComponents?deviceSerial=${serialNo}`,
      payload,
      customerDataApiRoot,
      jwt,
    );
  };

  editRegistrationStartDate = async (
    serialNo: SerialNumber,
    values: RegistrationDateValues,
    jwt: string,
  ): Promise<BaseResult> => {
    const payload = {
      serialNo: serialNo.toString(),
      RegistrationStartDate: values.registrationDate,
    };

    return await post(
      `CustomerData/EditRegistrationStartDate`,
      payload,
      customerDataApiRoot,
      jwt,
    );
  };

  addNewPin = async (
    newPinData: AddNewPinPost,
    jwt: string,
  ): Promise<AddNewPinResponse> =>
    await post("CustomerData/Pin", newPinData, customerDataApiRoot, jwt);

  addAdditionalAccessGroup = async (
    data: addAdditionalAccessGroupPost,
    jwt: string,
  ): Promise<BaseResult> =>
    await post("CustomerData/AccessGroup", data, customerDataApiRoot, jwt);

  addDeviceToAccessGroup = async (
    newDeviceData: AddDeviceToAccessGroupProps,
    jwt: string,
  ): Promise<BaseResult> =>
    await post(
      "CustomerData/AccessGroup/AddDevice",
      newDeviceData,
      customerDataApiRoot,
      jwt,
    );

  removeDeviceFromAccessGroup = async (
    payload: RemoveDeviceFromAccessGroup,
    jwt: string,
  ): Promise<BaseResult> =>
    await del(
      "CustomerData/AccessGroup/RemoveDevice",
      customerDataApiRoot,
      jwt,
      payload,
    );

  removeAccessGroup = async (
    payload: RemoveAccessGroup,
    jwt: string,
  ): Promise<BaseResult> =>
    await del("CustomerData/AccessGroup", customerDataApiRoot, jwt, payload);

  editPin = async (data: EditPin, jwt: string): Promise<BaseResult> =>
    await put("CustomerData/Pin", data, customerDataApiRoot, jwt);

  removePin = async (payload: RemovePin, jwt: string): Promise<BaseResult> =>
    await del("CustomerData/Pin", customerDataApiRoot, jwt, payload);
}

const customerDataApi = new CustomerDataApi();

export default customerDataApi;
